/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import PurchaserForm from './PurchaserForm';
import 'styles/components/purchase/PaymentOptions';
import PaymentOptionsType from './PaymentOptionsType';
import { CheckoutAccountActivation } from './CheckoutAccountActivation';
import QuickCheckoutAgreementForm from './QuickCheckout/QuickCheckoutAgreementForm';

/**
 * PaymentOptions component.
 * Renders payment options and purchaser form based on configuration.
 * The purchaser form can be hidden for exchange tickets using the HIDE_EXCHANGE_PURCHASER_FORM feature flag.
 * @param {object} props - Component props
 * @param {function} props.submitPurchaser - Function to handle purchaser form submission
 * @param {boolean} props.isExchange - Flag indicating if this is an exchange transaction
 * @param {boolean} props.removeTopMargin - Flag to remove top margin from container
 * @param {boolean} props.hidePurchaserForm - Flag to hide purchaser form (overrides feature flag)
 */
const PaymentOptions = (props) => {
  const { features } = useSelector((state) => state.whitelabelConfig);
  const {
    submitPurchaser,
    onSubmitPurchaserFail,
    isExchange,
    removeTopMargin,
    hidePurchaserForm = false,
    hidePaymentMethods = false,
    collapsiblePurchaserForm = false,
    purchaserFormDefaultExpanded = false,
    quickCheckoutAgreementPolicy,
    purchaserAgreementContent,
  } = props;

  const SHOW_PURCHASER_FORM = features.PURCHASER_FORM_ON === 'CHECKOUT';
  const showInvoiceToggle = features.ENABLE_INVOICE_IN_CHECKOUT;

  // Hide PurchaserForm if it's an exchange and the feature flag is enabled or if hidePurchaserForm is enabled
  const shouldHidePurchaserForm =
    hidePurchaserForm || (isExchange && features.HIDE_EXCHANGE_PURCHASER_FORM);

  // Agreements render after the payment methods, as the last step before paying. When the
  // buyer form owns them, a portal keeps the Fields inside that redux-form instance
  // (validation, remote submit, touch-on-fail) while their DOM lands in the slot below.
  // The slot node only exists after mount, hence the callback ref.
  const [agreementSlot, setAgreementSlot] = useState(null);
  const portalsAgreements =
    SHOW_PURCHASER_FORM && !shouldHidePurchaserForm && Boolean(purchaserAgreementContent);
  let buyerAgreementContent = purchaserAgreementContent;
  if (portalsAgreements) {
    buyerAgreementContent = agreementSlot
      ? createPortal(purchaserAgreementContent, agreementSlot)
      : null;
  }

  return (
    <div className={removeTopMargin ? 'payment-content remove-top-margin' : ''}>
      {SHOW_PURCHASER_FORM && (
        <div className={shouldHidePurchaserForm ? 'hidden-purchaser-form' : ''}>
          <PurchaserForm
            onSubmit={submitPurchaser}
            onSubmitPurchaserFail={onSubmitPurchaserFail}
            showPurchaserForm={SHOW_PURCHASER_FORM}
            isExchange={isExchange}
            showInvoiceToggle={showInvoiceToggle}
            collapsible={collapsiblePurchaserForm}
            defaultExpanded={purchaserFormDefaultExpanded}
            quickCheckoutAgreementPolicy={quickCheckoutAgreementPolicy}
            purchaserAgreementContent={buyerAgreementContent}
          />
        </div>
      )}

      <CheckoutAccountActivation isExchange={isExchange} />

      {!hidePaymentMethods && <PaymentOptionsType {...props} />}

      {portalsAgreements && <div ref={setAgreementSlot} className="mt-6" />}
      {!SHOW_PURCHASER_FORM && quickCheckoutAgreementPolicy?.hasAgreements && (
        <div className="mt-6">
          <QuickCheckoutAgreementForm
            onSubmit={submitPurchaser}
            onSubmitPurchaserFail={onSubmitPurchaserFail}
            quickCheckoutAgreementPolicy={quickCheckoutAgreementPolicy}
            purchaserAgreementContent={purchaserAgreementContent}
          />
        </div>
      )}
    </div>
  );
};

PaymentOptions.propTypes = {
  availablePayments: PropTypes.array.isRequired,
  onPaymentTypeChange: PropTypes.func.isRequired,
  submitPurchaser: PropTypes.func.isRequired,
  onSubmitPurchaserFail: PropTypes.func,
  isOpenTicket: PropTypes.bool.isRequired,
  selectedOption: PropTypes.string.isRequired,
  cardErrorOccured: PropTypes.bool,
  initialPurchaserValues: PropTypes.object,
  isExchange: PropTypes.bool,
  removeTopMargin: PropTypes.bool,
  hidePurchaserForm: PropTypes.bool,
  hidePaymentMethods: PropTypes.bool,
  collapsiblePurchaserForm: PropTypes.bool,
  purchaserFormDefaultExpanded: PropTypes.bool,
  quickCheckoutAgreementPolicy: PropTypes.object,
  purchaserAgreementContent: PropTypes.node,
};

export default PaymentOptions;
